import { Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormAvatarComponent, FormInputComponent, FormPasswordComponent, FormSelectComponent, FormTextareaComponent } from '@shared/forms';
import { UserFacade } from '@app/store/user';
import { injectOrganisationOptions, injectSchoolOptions } from '@app/store/form-options';
import { CountryCode, COUNTRY_OPTIONS, getUserRoleOptions, USER_ROLES, UserRole } from '@shared/types';
import { toScopeValue, updateScopeValidators } from '../user-scope';

@Component({
    selector: 'app-user-add-modal',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormAvatarComponent,
        FormInputComponent,
        FormPasswordComponent,
        FormSelectComponent,
        FormTextareaComponent,
    ],
})
export class AddComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly userFacade = inject(UserFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    readonly saving = this.userFacade.saving;

    readonly roleOptions = getUserRoleOptions();
    readonly countryOptions = COUNTRY_OPTIONS;
    readonly schoolOptions = injectSchoolOptions();
    readonly organisationOptions = injectOrganisationOptions();

    readonly form = this.formBuilder.group({
        firstName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        lastName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        email: this.formBuilder.control('', [FormValidators.Required, FormValidators.Email]),
        role: this.formBuilder.control<UserRole>('teacher'),
        description: this.formBuilder.control(''),
        country: this.formBuilder.control<CountryCode | null>(null, [FormValidators.Required]),
        schoolId: this.formBuilder.control<string | null>(null, [FormValidators.Required]),
        organisationId: this.formBuilder.control<string | null>(null),
        avatarId: this.formBuilder.control<string | null>(null),
        contactEmail: this.formBuilder.control('', [FormValidators.Email]),
        password: this.formBuilder.control('', [FormValidators.Password({ minLength: 8, lowercaseLetters: 1, uppercaseLetters: 1, numbers: 1, symbols: 1 })]),
    });

    private readonly roleValue = toSignal(this.form.controls.role.valueChanges, { initialValue: this.form.controls.role.value });
    readonly isSchoolAdmin = computed(() => this.roleValue() === 'admin');
    readonly isOrganisationAdmin = computed(() => this.roleValue() === 'organisation_admin');

    constructor() {
        this.prefillFromUrl();

        effect(() => {
            updateScopeValidators(this.form.controls, this.isOrganisationAdmin());
        });

        effect(() => {
            if (this.userFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.userFacade.saving() === false && this.userFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.userFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            this.form.disable();
            const value = this.form.getRawValue();
            this.userFacade.createUser({
                firstName: value.firstName,
                lastName: value.lastName,
                email: value.email,
                role: value.role,
                description: value.description || undefined,
                country: value.country,
                ...toScopeValue(value, this.isOrganisationAdmin()),
                avatarId: value.avatarId,
                contactEmail: value.role === 'admin' ? value.contactEmail || null : null,
                password: value.password || undefined,
            });
        }
    }

    /** Optional initial values from the modal URL, e.g. `(modal:users/add;schoolId=X;role=admin)` or `(modal:users/add;role=organisation_admin;organisationId=X;firstName=A;lastName=B;email=C)`. */
    private prefillFromUrl(): void {
        const params = this.route.parent?.snapshot.params ?? {};
        const role = params['role'];

        if (USER_ROLES.includes(role)) {
            this.form.controls.role.setValue(<UserRole>role);
        }

        const { schoolId, organisationId, firstName, lastName, email } = this.form.controls;
        for (const [key, control] of Object.entries({ schoolId, organisationId, firstName, lastName, email })) {
            if (params[key]) {
                control.setValue(params[key]);
            }
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
